import { useDataGridConfig } from '@/hooks/useDataGridConfig'
import http from '@/lib/http'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import type { GridFilterItem, GridSortModel } from '@mui/x-data-grid'
import { buildFilterParams, type FilterFieldMapping, type SortFieldMapping } from '@/lib/dataGridFilters'

/**
 * Field mappings for med request DataGrid filters.
 * Maps DataGrid column fields to backend filter parameter names.
 */
export const MED_REQUEST_FILTER_FIELD_MAPPING: FilterFieldMapping = {
  orderItemStatus: { paramName: 'orderItemStatusFilter', type: 'text' },
  medicationName: { paramName: 'medicationNameFilter', type: 'text' },
  patientFullName: { paramName: 'patientFullNameFilter', type: 'text' },
  modifiedAt: { paramName: 'modifiedAtFilter', type: 'date' },
  orderNumber: { paramName: 'orderNumberFilter', type: 'text' },
  csNotes: { paramName: 'csNotesFilter', type: 'text' },
  pharmacistNotes: { paramName: 'pharmacistNotesFilter', type: 'text' },
  patientResponse: { paramName: 'patientResponseFilter', type: 'boolean' }
}

/**
 * Field mappings for med request DataGrid sorting.
 * Maps DataGrid column fields to backend sort field names.
 */
export const MED_REQUEST_SORT_FIELD_MAPPING: SortFieldMapping = {
  patientFullName: 'patient_full_name',
  medicationName: 'medication_name',
  modifiedAt: 'modified_at'
}

/** API response shape for a single med request row (snake_case) */
export type APIMedReqListItem = {
  orderItemHash: string
  orderItemStatus: string | null
  orderNumber: string | null
  orderType: string | null
  productHash: string | null
  productName: string | null
  productGroup: string | null
  quantity: number | null
  /** Product shipping weight in kilograms (from product_property.weight); null when unset. */
  productWeight: number | null
  patientFullName: string | null
  medicationName: string | null
  modifiedAt: string | null
  createdAt: string | null
  lastUpdatedBy: { id: string; name: string } | null
  amp: string | null
  vmp: string | null
  ampmVmpp: string | null
  prescribedItemName: string | null
}

export function toMedReqListItem(apiMedReq: APIMedReqListItem): MedReqListItem {
  return {
    ...apiMedReq,
    id: apiMedReq.orderItemHash,
    hash: apiMedReq.orderItemHash
  }
}

/** API response shape for the list endpoint */
export type GetMedReqsAPIResponse = {
  medReqs: APIMedReqListItem[]
  count: number
}

/** Frontend med request list item (camelCase) */
export type MedReqListItem = APIMedReqListItem & {
  // `id` is required by MUI DataGrid
  id: string
  hash: string
}

export type FetchMedReqsParams = {
  searchQuery?: string
  skip?: number
  limit?: number

  // Advanced filters (JSON-encoded)
  orderItemStatusFilter?: string
  medicationNameFilter?: string
  patientFullNameFilter?: string
  modifiedAtFilter?: string
  csNotesFilter?: string
  pharmacistNotesFilter?: string

  // Boolean filters (not JSON-encoded)
  /** `false` = FANned, with no patient response recorded on any hold entry. */
  patientResponseFilter?: string

  // Sorting
  sortBy?: 'patient_full_name' | 'medication_name' | 'modified_at'
  sortByDirection?: 'asc' | 'desc'
}

export type FetchMedReqsResponse = {
  medReqs: MedReqListItem[]
  count: number
}

/**
 * Fetch med requests with optional search, filters, and pagination.
 * @returns Paginated list of med requests with total count.
 */
export const fetchMedReqs = async (
  params: FetchMedReqsParams = {},
  signal?: AbortSignal
): Promise<FetchMedReqsResponse> => {
  const { medReqs, count } = (await http.get('/med-reqs', {
    params,
    signal
  })) as GetMedReqsAPIResponse

  return {
    medReqs: medReqs.map(toMedReqListItem),
    count
  }
}

/**
 * Fetch med requests scoped to a specific patient hash.
 */
export const fetchMedReqsByPatientHash = async (patientHash: string): Promise<MedReqListItem[]> => {
  const { medReqs } = (await http.get('/med-reqs', {
    params: {
      searchQuery: patientHash,
      searchAttributes: JSON.stringify(['patient_hash'])
    }
  })) as GetMedReqsAPIResponse

  return medReqs.map(toMedReqListItem)
}

/**
 * Fetch med requests scoped to a specific order hash.
 */
export const fetchMedReqsByOrderHash = async (orderHash: string): Promise<MedReqListItem[]> => {
  const { medReqs } = (await http.get('/med-reqs', {
    params: {
      searchQuery: orderHash,
      searchAttributes: JSON.stringify(['order_hash'])
    }
  })) as GetMedReqsAPIResponse

  return medReqs.map(toMedReqListItem)
}

/**
 * Hook to fetch med requests by patient hash.
 */
export const useMedReqsByPatientHashQuery = (patientHash: string | undefined) => {
  return useQuery({
    queryKey: ['med-requests', 'patient', patientHash],
    queryFn: () => fetchMedReqsByPatientHash(patientHash!),
    enabled: !!patientHash
  })
}

/**
 * Hook to fetch med requests by order hash.
 */
export const useMedReqsByOrderHashQuery = (orderHash: string | undefined) => {
  return useQuery({
    queryKey: ['med-requests', 'order', orderHash],
    queryFn: () => fetchMedReqsByOrderHash(orderHash!),
    enabled: !!orderHash
  })
}

/**
 * Hook to fetch med requests with DataGrid integration.
 * Supports search query, column filters, sorting, and pagination.
 */
export const useMedReqsQuery = ({ tableId = 'med-requests' }: { tableId?: string }) => {
  const dataGridConfig = useDataGridConfig({ tableId, syncToUrl: true })

  const queryParams: FetchMedReqsParams = useMemo(() => {
    const { paginationModel, filters, filterModel, sortModel } = dataGridConfig

    // Build pagination params
    const params: FetchMedReqsParams = {
      skip: paginationModel ? paginationModel.page * paginationModel.pageSize : 0,
      limit: paginationModel?.pageSize ?? 10
    }

    // Build filter and sort params using the DRY utility
    const filterParams = buildFilterParams({
      searchQuery: filterModel?.quickFilterValues?.join(' '),
      filterItems: filters as GridFilterItem[],
      sortItems: (sortModel ?? []) as GridSortModel[number][],
      filterFieldMapping: MED_REQUEST_FILTER_FIELD_MAPPING,
      sortFieldMapping: MED_REQUEST_SORT_FIELD_MAPPING
    })

    return { ...params, ...filterParams }
  }, [dataGridConfig])

  const { data, isLoading, isFetching, isError, error } = useQuery({
    queryKey: ['med-requests', queryParams],
    queryFn: ({ signal }) => fetchMedReqs(queryParams, signal),
    placeholderData: keepPreviousData
  })

  const medReqs = useMemo(
    () =>
      (data?.medReqs ?? []).map(item => ({
        ...item,
        modifiedAt: item.modifiedAt ? new Date(item.modifiedAt) : null
      })),
    [data?.medReqs]
  )

  return {
    medReqs,
    count: data?.count ?? 0,
    isLoading,
    isFetching,
    isError,
    error,
    dataGridConfig
  }
}
